import { type CapabilityConfig, type Message, MessageClass } from './types';

const VALID_MESSAGE_CONFIG_KEYS = ['class', 'type', 'payload'] as const;

export function hasOnlyKeysFromSet(
  obj: Record<string, unknown>,
  validKeys: readonly string[],
): boolean {
  const keys = Object.keys(obj);
  return keys.every((k) => validKeys.includes(k));
}

export function hasOnlyValuesFromSet(
  values: unknown[],
  validKeys: readonly unknown[],
): boolean {
  return values.every((k) => validKeys.includes(k));
}

/**
 * Determine if key exists in config, taking to account null (it is returned for all fields without value from api)
 */
export function hasValue<K extends string>(
  obj: Record<string, unknown>,
  key: K,
): obj is { [key in K]: unknown } {
  return key in obj && obj[key] !== null;
}

/**
 * Check if messages config is valid
 */
export function isValidMessageConfig(config: unknown): config is Message {
  if (typeof config !== 'object' || config === null) {
    return false;
  }

  if (
    !hasOnlyKeysFromSet(
      config as Record<string, unknown>,
      VALID_MESSAGE_CONFIG_KEYS,
    )
  ) {
    return false;
  }

  if (!('type' in config) || typeof config.type !== 'string') {
    return false;
  }

  if (
    !('class' in config) ||
    (config.class !== MessageClass.Action &&
      config.class !== MessageClass.Event)
  ) {
    return false;
  }

  return true;
}

/**
 * Check if context config is valid
 */
export function isValidContextConfig(
  config: CapabilityConfig,
  validContextKeys: readonly string[],
): boolean {
  if ('context' in config) {
    if (!Array.isArray(config['context'])) {
      return false;
    }

    // Config must only contain valid context values
    if (!hasOnlyValuesFromSet(config['context'], validContextKeys)) {
      return false;
    }
  }

  return true;
}

/**
 * Check if context is valid
 */
export function isValidContext<Context>(
  context: unknown,
  validContextKeys: readonly string[],
  requiredContextKeys: readonly string[] = [],
): context is Context {
  // check if context is object
  if (typeof context !== 'object' || context === null) {
    return false;
  }

  for (const [key, val] of Object.entries(context)) {
    // check if context includes only allowed keys
    if (!validContextKeys.includes(key)) {
      return false;
    }
    // check if value is present but a string
    if (val !== undefined && typeof val !== 'string') {
      return false;
    }
  }

  // check if every required key is present
  return requiredContextKeys.every(
    (key) => (context as Record<string, string>)[key] !== undefined,
  );
}
